import { useNavigate, useParams } from "@tanstack/react-router";

import {
  useReservoir,
  useUpdateReservoir,
} from "@/lib/api/reservoirs";
import { ApiError } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";
import { OptimisticLockBanner } from "@/components/optimistic-lock-banner";

import {
  ReservoirForm,
  emptyToUndef,
  parseOptNum,
  type ReservoirFormValues,
} from "./form";

export function ReservoirEditPage() {
  const { reservoirId } = useParams({
    from: "/reservoirs/$reservoirId/edit",
  });
  const navigate = useNavigate();
  const q = useReservoir(reservoirId);
  const update = useUpdateReservoir(reservoirId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  if (q.data.system_provided) {
    return (
      <p className="text-sm text-muted-foreground">
        System reservoirs are read-only; clone to a tenant copy first.
      </p>
    );
  }

  function submit(values: ReservoirFormValues) {
    if (!q.data) return;
    update.mutate(
      {
        expected_updated_at: q.data.updated_at,
        name: values.name,
        container_type: values.container_type,
        inside_l_in: parseOptNum(values.inside_l_in),
        inside_w_in: parseOptNum(values.inside_w_in),
        inside_h_in: parseOptNum(values.inside_h_in),
        cross_section_in2: parseOptNum(values.cross_section_in2),
        gal_per_inch: parseOptNum(values.gal_per_inch),
        cm_per_gal: parseOptNum(values.cm_per_gal),
        notes: emptyToUndef(values.notes),
        reason: emptyToUndef(values.reason),
      },
      {
        onSuccess: () =>
          navigate({
            to: "/reservoirs/$reservoirId",
            params: { reservoirId },
          }),
      },
    );
  }

  const stale =
    update.error instanceof ApiError && update.error.isStale;
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit reservoir</h1>
      <ReservoirForm
        initial={q.data}
        submitting={update.isPending}
        showReason
        submitLabel="Save changes"
        onSubmit={submit}
        banner={
          stale ? (
            <OptimisticLockBanner
              error={update.error}
              onRefresh={() => {
                update.reset();
                void q.refetch();
              }}
            />
          ) : undefined
        }
      />
      {update.error && !stale && (
        <ErrorState error={update.error} retry={() => update.reset()} />
      )}
    </div>
  );
}
